/**
 * Actions related to authentication and user management
 *
 * Note: Any exported action that can dispatch setAuthError should dispatch
 * setAuthError() at the beginning of the action.
 */
import * as Cookies from 'js-cookie';

import { StoreThunk } from './types';
import {
  AuthData,
  AuthError,
  RegistrationData,
  SetupState,
  UserInfo,
  PasswordLoginOptions,
  AuthErrorType,
  LoginExceptions,
  NotAuthorizedSubstrings
} from '../types';
import { setEmailVerificationData } from './index';
import {
  getAuthData,
  getUserSetupState,
  getAccessToken,
  getRegistrationData,
  getBranding
} from '../reducers';
import getAuthModule, * as auth from '../auth';
import history from '../history';
import * as built from '../built';
import {
  parseJWTToken,
  getConfig,
  getAuthError,
  getCookieOptions
} from '../utils';
import { CognitoUserSession } from '@built/amazon-cognito-identity-js';
import l10n from '@built/i18n';

export const SET_AUTH_DATA: 'SET_AUTH_DATA' = 'SET_AUTH_DATA';
export const SET_REGISTRATION_DATA: 'SET_REGISTRATION_DATA' =
  'SET_REGISTRATION_DATA';
export const SET_AUTH_ERROR: 'SET_AUTH_ERROR' = 'SET_AUTH_ERROR';

export interface SetAuthData {
  type: typeof SET_AUTH_DATA;
  authData?: AuthData;
}

export interface SetRegistrationData {
  type: typeof SET_REGISTRATION_DATA;
  registrationData?: RegistrationData;
}

export interface SetAuthError {
  type: typeof SET_AUTH_ERROR;
  error?: AuthError;
}

// the name of the cookie that console sets upon logout
// to help us redirect the user back to the appropriate
// place
const BUILT_REDIRECT_COOKIE = 'builtRedirect';

// LOGIN-186
const BUILT_LOGOUT_COOKIE = 'builtLogoutUrl';

const authModule = getAuthModule();

/**
 * Set the current authorization data for the logged in user.
 */
export function setAuthData(authData?: AuthData) {
  return {
    type: SET_AUTH_DATA,
    authData
  };
}

/**
 * Set the current registration data for user registration/signup flow.
 */
export function setRegistrationData(registrationData?: RegistrationData) {
  return {
    type: SET_REGISTRATION_DATA,
    registrationData
  };
}

/**
 * Set the current authorization error.
 */
export function setAuthError(error?: AuthError) {
  return {
    type: SET_AUTH_ERROR,
    error
  };
}

/**
 * Login a user. This will involve multiple browser redirections and will
 * eventually result in some local state updates.
 * @param allowExpiredError allow handling expired user migration (Default: true)
 */
export function login(
  options: auth.LoginOptions,
  allowExpiredError: boolean = true
): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    const opts = auth.isPasswordLogin(options)
      ? options
      : { mfaType: getAuthData(getState()).needsMfa!, ...options };

    dispatch(setAuthError());

    return authModule
      .login(opts)
      .catch((err: Error) => {
        if (allowExpiredError) {
          const credentials = <PasswordLoginOptions>options;

          return isExpiredError(err, credentials.username)
            .then((status: boolean) => {
              if (status) {
                // If login attempt results in NotAuthorizedSubstrings.AccountExpired
                // call the API to unexpire the user and make a recursive call, ONCE more
                // to attempt to log in the user.
                dispatch(login(options, false));
                return Promise.resolve(true);
              }

              return Promise.resolve(false);
            })
            .catch((error: Error) => {
              return handleLoginError(err, opts);
            });
        } else {
          return handleLoginError(err, opts);
        }
      })
      .then(authData => {
        // LOGIN-184: keep existing auth data state so we don't
        // accidentally lose the ability to migrate IMA users.
        // In the future, we need to be aware of the current
        // state and incoming state changes to make sure we
        // have exactly what's needed after a successful auth.
        dispatch(
          setAuthData({
            ...getState().authData,
            ...(<AuthData>authData)
          })
        );
        return true;
      })
      .catch((authError: Error) => {
        dispatch(setAuthError(authError));
        return false;
      });
  };
}

/**
 * Attempt to call API unexpire method if error results in NotAuthorizedSubstrings.AccountExpired
 * @param error
 * @param email (optional)
 */
function isExpiredError(error: Error, email?: string): Promise<boolean> {
  if (error.message.indexOf(NotAuthorizedSubstrings.AccountExpired) > -1) {
    return new Promise((resolve, reject) => {
      if (!email) {
        reject(false);
      } else {
        built
          .unexpire(email)
          .then(() => {
            resolve(true);
          })
          .catch(() => {
            // Fall thru to handle 408 or 500 responses
            reject(false);
          });
      }
    });
  }

  return Promise.reject(false);
}

function handleLoginError(
  err: Error,
  opts: auth.LoginOptions
): Promise<AuthData> {
  if (
    err.name === LoginExceptions.UserNotFoundException &&
    auth.isPasswordLogin(opts)
  ) {
    // user was not found in the user pool, so we attempt a user migration
    // with Built API
    return migrateUser(opts).catch(() => {
      return Promise.reject(
        getAuthError(AuthErrorType.INVALID_CREDENTIALS_ERROR)
      );
    });
  } else if (err.name === LoginExceptions.PasswordResetRequiredException) {
    return Promise.reject(
      getAuthError(AuthErrorType.PASSWORD_RESET_REQUIRED_ERROR)
    );
  } else if (err.name === LoginExceptions.NotAuthorizedException) {
    return Promise.reject(handleNotAuthorizedException(err));
  } else if (err.name === LoginExceptions.MockCognitoException) {
    // A generic error thrown by the mockCognito module when
    // the email address provided wasn't found
    return Promise.reject(
      getAuthError(
        AuthErrorType.MOCK_COGNITO_ERROR,
        err.message,
        l10n.t('Auth:Notification.userNotFound', 'User not found')
      )
    );
  } else if (err.name === LoginExceptions.CodeMismatchException) {
    return Promise.reject(getAuthError(AuthErrorType.MFA_VERIFICATION_ERROR));
  } else {
    // Unknown error occurred
    return Promise.reject(getAuthError(AuthErrorType.UNKNOWN_ERROR));
  }
}

/**
 * Handles the sub errors of the NotAuthorizedException from Cognito
 * @param err
 */
export function handleNotAuthorizedException(err: Error) {
  if (err.message.indexOf(NotAuthorizedSubstrings.AttemptsExceeded) > -1) {
    // user's account has been temporarily disabled due to too many attempts
    return getAuthError(AuthErrorType.ATTEMPTS_EXCEEDED_ERROR);
  } else if (
    err.message.indexOf(NotAuthorizedSubstrings.AccountDisabled) > -1
  ) {
    // user's account has been disabled by an admin
    return getAuthError(AuthErrorType.ACCOUNT_DISABLED);
  } else if (err.message.indexOf(NotAuthorizedSubstrings.AccountExpired) > -1) {
    // user's account expired before providing initial password
    return getAuthError(AuthErrorType.ACCOUNT_EXPIRED);
  } else if (err.message.indexOf(NotAuthorizedSubstrings.InvalidState) > -1) {
    return getAuthError(AuthErrorType.ACCOUNT_EXPIRED);
  } else if (err.message.indexOf(NotAuthorizedSubstrings.SessionExpired) > -1) {
    return getAuthError(AuthErrorType.SESSION_EXPIRED);
  }

  // anything else is considered invalid credentials
  return getAuthError(AuthErrorType.INVALID_CREDENTIALS_ERROR);
}

/**
 * Handles the LimitExceededException from Cognito
 * @param err
 */
export function handleLimitExceededException(err: Error) {
  return getAuthError(AuthErrorType.LIMIT_EXCEEDED_ERROR);
}

function migrateUser(opts: PasswordLoginOptions) {
  // Check against the built server whether this is a user
  // that can be migrated to Cognito, and log in after they
  // are migrated if possible
  return built
    .migrate(opts.username, opts.password)
    .then(() => authModule.login(opts));
}

/**
 * Logout the current user.
 */
export function logout(redirect = true): StoreThunk {
  return dispatch => {
    authModule.logout();
    dispatch(setAuthData());
    dispatch(setAuthError());
    dispatch(setEmailVerificationData());
    if (redirect) {
      history.push('/');
    }
  };
}

/**
 * Save Cognito user data
 */
export function saveUserData(info: UserInfo): StoreThunk {
  return dispatch => dispatch(setUserAttributes({ ...info }));
}

/**
 * Tell Built that a user has agreed to ToS
 */
export function agreeToTos(agreedToTos = true): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    const accessToken = getAccessToken(getState());

    if (accessToken) {
      dispatch(setAuthError());
      return built
        .agreeToTos(accessToken)
        .then(() => {
          dispatch(
            setAuthData({
              ...getAuthData(getState()),
              agreedToTos
            })
          );
        })
        .then(() => true)
        .catch(error => {
          dispatch(setAuthError(error));
          return false;
        });
    }

    dispatch(
      setAuthError({ message: 'Unable to set ToS, user is not signed in' })
    );
    return Promise.resolve(false);
  };
}

/**
 * Determine whether a given AuthData object indicates that a user is currently
 * authorized.
 */
export function isAuthorized(authData: AuthData) {
  if (authData && authData.idToken && authData.refreshToken) {
    return true;
  }
  return false;
}

/**
 * Route a user based on their authentication state
 */
export function routeUser(): StoreThunk<Promise<boolean>> {
  // TODO (abe) simplify routing and remove tslint ignore below
  // tslint:disable-next-line:cyclomatic-complexity
  return (dispatch, getState) => {
    const state = getState();
    const authData = getAuthData(state) || {};
    let promise = Promise.resolve(true);

    if (authData.maintenanceMode === true) {
      // when BAPI is in maintenance mode, we need to direct them to
      // our maintenance mode view
      history.push({ pathname: '/maintenance', search: location.search });
    } else if (isAuthorized(authData)) {
      promise = dispatch(handleAuthenticatedUser());
    } else if (authData.email && authData.registrationToken) {
      // If we have an email address and valid registration token, then
      // the user needs to finish their initial account setup
      history.push({ pathname: '/setup', search: location.search });
    } else if (location.pathname.indexOf('/reset') !== 0) {
      // Otherwise, the user can only login or reset their password
      history.push({ pathname: '/', search: location.search });
    }

    return promise;
  };
}

/**
 * This function takes the provided AuthData and enforces MFA
 * setup if enabled; if MFA is complete or disabled, the
 * appropriate cookies are set and the user is redirected to
 * console.
 *
 * @param immediate only applicable during welcome back flows
 */
export function handleAuthenticatedUser(
  immediate = false
): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    const state = getState();
    const authData = state.authData;
    const setupState = getUserSetupState(state);
    let initialize = true;

    if (authData.welcomeBack) {
      if (immediate) {
        // LOGIN-182: if immediate redirect is required, do so
        dispatch(redirectToConsole());
        initialize = false;
      } else {
        // if the user arrived to the login app after already authenticating
        // then we want to prevent the automatic redirection to console and
        // a "welcome back" view should be shown
        history.replace({ pathname: '/', search: location.search });
      }
    } else if (authData.email && setupState !== SetupState.complete) {
      // The user has an account but they have not completed
      // account setup
      history.replace({ pathname: '/setup', search: location.search });
    } else if (isMfaRequired(authData.idToken)) {
      // if MFA is required and the user hasn't completed their MFA setup
      // we need to kick them over to the setup view
      history.replace({ pathname: '/setup', search: location.search });
    } else {
      // all good, redirect them to console
      dispatch(redirectToConsole());
      initialize = false;
    }

    return Promise.resolve(initialize);
  };
}

export function redirectToConsole(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    // LOGIN-186 when using a branded login, set the appropriate logout
    // URL in a cookie so console can send the user back to the right spot
    const cookieOptions = getCookieOptions();
    const authData = getAuthData(getState());

    if (authData && authData.ssoClientId) {
      // LOGIN-194 SSO users need to sign out through Cognito
      // which will redirect them back to the logout endpoint of this
      // app; here we build up the appropriate logout URL to use and
      // set that in the logout cookie that console uses
      const region = getConfig('REACT_APP_REGION');
      const userPoolDomainPrefix = getConfig(
        'REACT_APP_USER_POOL_DOMAIN_PREFIX'
      );
      const logoutUrl = `https://${userPoolDomainPrefix}.auth.${region}.amazoncognito.com/logout?client_id=${
        authData.ssoClientId
      }&logout_uri=https://${location.hostname}/logout`;
      Cookies.set(BUILT_LOGOUT_COOKIE, logoutUrl, cookieOptions);
    } else if (getBranding(getState())) {
      // Users should return back to the branded login UX when they
      // logout of console
      const logoutUrl = `${location.origin}/logout`;
      Cookies.set(BUILT_LOGOUT_COOKIE, logoutUrl, cookieOptions);
    } else {
      Cookies.remove(BUILT_LOGOUT_COOKIE, cookieOptions);
    }

    // redirect to console
    window.location.href = getRedirectUrl();

    return Promise.resolve(true);
  };
}

/**
 * Enable SMS MFA when the user already has a verified phone number
 */
export function enableMfa(type: 'sms' | 'totp'): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());

    return authModule
      .enableMfa(type)
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch(error => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

/**
 * Disable MFA on the current user's account
 */
export function disableMfa(): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());

    return authModule
      .disableMfa()
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch(error => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

/**
 * Set a new user's initial password
 */
export function setInitialPassword(
  newPassword: string
): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    dispatch(setAuthError());

    return authModule
      .setInitialPassword(newPassword)
      .then(authData => {
        return dispatch(
          setAuthData({
            ...getState().authData,
            ...authData,
            newPasswordRequired: false
          })
        );
      })
      .then(() => true)
      .catch(error => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

/**
 * Sets the user-provided email address
 */
export function setEmailAddress(email: string): StoreThunk<void> {
  return (dispatch, getState) => {
    dispatch(
      setAuthData({
        ...getState().authData,
        userProvidedEmail: email
      })
    );
  };
}

/**
 * Starts the user sign up process; upon successful sign up,
 * the user will be automatically signed in
 */
export function signUpUser(
  username: string,
  password: string,
  registrationToken: string
): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    dispatch(setAuthError());

    return authModule
      .userSignUp(username, password, registrationToken)
      .then(result => {
        // user has registered, now log them in to collect and
        // store MFA settings
        return authModule.login({ username, password }).then(authData => {
          // user is logged in, now associate their federated identity
          return built.associateUserAccount(
            authData.idToken!,
            registrationToken
          );
        });
      })

      .then(() => {
        // user has has signed up and logged in; we don't need to collect
        // a password from them from this point forward in the process, so
        // update our auth data state
        const authData = getAuthData(getState());
        authData.newPasswordRequired = false;
        setAuthData(authData);
        dispatch(setAuthData(authData));
        return true;
      })
      .catch(error => {
        dispatch(setAuthError({ ...error, title: 'Sign up failure' }));
        return false;
      });
  };
}

/**
 * Associates an auth provider user with a built api user. The user
 * must already have been authenticated with the auth provider AND
 * this can only be called during the registration/signup process (
 * ideally at the end of the flow).
 */
export function associateUser(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    dispatch(setAuthError());

    const registrationData = getRegistrationData(getState());
    return authModule
      .getAuthData()
      .then(userAuth => {
        return new Promise<boolean>((resolve, reject) => {
          // association requires an ID token from Cognito and
          // a Built registration token
          return built
            .associateUserAccount(
              userAuth!.idToken!,
              registrationData.registrationToken
            )
            .then(() => {
              resolve(true);
            });
        });
      })
      .catch(error => {
        dispatch(
          setAuthError({ ...error, title: 'Account association error' })
        );
        return false;
      });
  };
}

/**
 * Resend an SMS verification code
 */
export function resendSmsCode(): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());
    return authModule
      .resendSmsCode()
      .then(() => {
        return true;
      })
      .catch(error => {
        dispatch(setAuthError(error));
        return false;
      });
  };
}

export function setPhoneNumber(
  phoneNumber: string
): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());
    return dispatch(setUserAttributes({ phone_number: phoneNumber }));
  };
}

export function verifyPhoneNumber(
  code: string,
  useForMfa = false
): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());

    if (useForMfa) {
      // If useForMfa is set, verify the phone number and enable MFA in a
      // single step before updating user data
      return authModule
        .verifySmsPhoneNumber(code)
        .then(() => authModule.enableMfa('sms'))
        .then(() => dispatch(updateUserData()))
        .then(() => true)
        .catch(error => {
          const newError = getAuthError(AuthErrorType.MFA_VERIFICATION_ERROR);
          dispatch(setAuthError(newError));
          return false;
        });
    } else {
      return dispatch(verifyUserAttribute('phone_number', code));
    }
  };
}

export function verifySoftwareToken(
  code: string,
  useForMfa = false
): StoreThunk<Promise<boolean>> {
  return dispatch => {
    let promise = authModule.verifySoftwareToken(code);

    dispatch(setAuthError());

    if (useForMfa) {
      // If useForMfa is set, verify the token and enable MFA in a single
      // step before updating user data
      promise = promise.then(() => authModule.enableMfa('totp'));
    }

    return promise
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch(error => {
        const newError = getAuthError(AuthErrorType.MFA_VERIFICATION_ERROR);
        dispatch(setAuthError(newError));
        return false;
      });
  };
}

/**
 * Store all provided user attributes and retrieve the latest user data
 */
function setUserAttributes(attrs: {
  [name: string]: string;
}): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());

    return authModule
      .setUserAttributes(attrs)
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch(error => {
        dispatch(setAuthError(error.message));
        return false;
      });
  };
}

/**
 * Provide a code to validate a user attribute. Will generally be used for
 * email or phone number.
 */
function verifyUserAttribute(
  name: string,
  code: string
): StoreThunk<Promise<boolean>> {
  return dispatch => {
    dispatch(setAuthError());

    return authModule
      .verifyUserAttribute(name, code)
      .then(() => dispatch(updateUserData()))
      .then(() => true)
      .catch(error => {
        dispatch(setAuthError(error.message));
        return false;
      });
  };
}

/**
 * Retrieve the latest user data and update the local state with it
 */
function updateUserData(): StoreThunk<Promise<void>> {
  return (dispatch, getState) => {
    return authModule.getUserData().then(userData => {
      dispatch(
        setAuthData({
          ...getState().authData,
          data: userData
        })
      );
    });
  };
}

/**
 * Refreshes tokens for the active user
 */
export function refreshTokens(): StoreThunk<Promise<boolean>> {
  return (dispatch, getState) => {
    return authModule.getUser().then(({ user, session }) => {
      // grab the refresh token from the active user
      const refreshToken = session!.getRefreshToken();

      // refresh the tokens and return a promise
      return new Promise<boolean>((resolve, reject) => {
        user!.refreshSession(
          refreshToken,
          (error, newSession: CognitoUserSession) => {
            if (error) {
              return reject(error);
            }

            dispatch(
              setAuthData({
                ...getState().authData,
                idToken: newSession.getIdToken().getJwtToken(),
                accessToken: newSession.getAccessToken().getJwtToken(),
                refreshToken: newSession.getRefreshToken().getToken()
              })
            );
            return resolve(true);
          }
        );
      });
    });
  };
}

/**
 * When MFA is enabled, we expect the ID token to include
 * a special claim. Here we're checking if that claim is
 * set and if it's true or false
 *
 * @param idToken
 */
function isMfaRequired(idToken: string | undefined): boolean {
  if (!idToken) {
    return false;
  }

  const payload = parseJWTToken(idToken);
  const mfaRequired = payload.mfa_required && payload.mfa_required === 'true';
  if (!mfaRequired) {
    return false;
  }

  const mfaComplete = payload.mfa_complete && payload.mfa_complete === 'true';
  return !mfaComplete;
}

interface BuiltRedirect {
  // tslint:disable-next-line:no-any
  location: any;
  url: string;
}

/**
 * Loads, parses and validates the builtRedirect cookie to
 * determine where to redirect the user. If the cookie contains
 * a valid redirect, we'll use that, or else we'll send the
 * user to the default configured redirect URL.
 */
function getRedirectUrl(): string {
  const defaultRedirect = getConfig('REACT_APP_BUILT_CONSOLE_URL');
  const builtRedirect = Cookies.get(BUILT_REDIRECT_COOKIE);

  if (builtRedirect === undefined) {
    return defaultRedirect;
  }

  // remove the cookie after use
  Cookies.remove(BUILT_REDIRECT_COOKIE, {
    path: getConfig('REACT_APP_BUILT_COOKIE_PATH'),
    domain: getConfig('REACT_APP_BUILT_COOKIE_DOMAIN')
  });

  // LOGIN-178
  // attempt to parse `url` from the cookie and compare its
  // origin with the default redirect origin; if they match up
  // we can use it, or else we return null and act like it never
  // existed; this happens whenever a user has used multiple
  // environments and a builtRedirect is laying around
  try {
    // parse cookie
    const payload = JSON.parse(
      decodeURIComponent(builtRedirect)
    ) as BuiltRedirect;

    // and now compare the origins
    const cookieOrigin = getOrigin(payload.url);
    const defaultOrigin = getOrigin(defaultRedirect);
    if (cookieOrigin !== defaultOrigin) {
      // origins didn't match, use default redirect
      return defaultRedirect;
    }

    // use cookie-based redirect
    return payload.url;
  } catch (e) {
    // always return default redirect when errors occur
    return defaultRedirect;
  }
}

/**
 * IE11 doesn't support `URL` to nicely parse URLs, so
 * this will create an `a` element and use it to pull
 * the origin we need that works for all browsers.
 * @param url the URL to "parse"
 * @returns string containing the origin (ie, <scheme>://<domain>)
 */
function getOrigin(url: string): string {
  const a = document.createElement('a');
  a.href = url;
  return a.protocol + '//' + a.hostname;
}



// WEBPACK FOOTER //
// ./src/actions/auth.ts